import moment from 'moment-timezone';
import { FC, useEffect, useMemo } from 'react';

import Button from '@payaca/components/button/Button';
import { getTotalCompletedPaymentValueFromJobPayments } from '@payaca/helpers/jobPaymentHelper';

import { currencyPrice } from '@/helpers/financeHelper';
import {
  getSpecificJobTypeText,
  JobTypeSpecificText,
} from '@/helpers/jobHelper';
import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';

import { useSelector } from '@/api/state';
import TextareaField from '@payaca/components/textareaField/TextareaField';
import { MOMENT_DATE_SHORT_READABLE_FORMAT } from '@payaca/constants/momentFormatConstants';
import { Customer } from '@payaca/types/customerTypes';
import { JobContent } from '@payaca/types/jobContentTypes';
import { JobPayment } from '@payaca/types/jobPaymentTypes';
import { Job } from '@payaca/types/jobTypesV2';

type Props = {
  emailTemplate: string;
  job: Job;
  jobContent: JobContent;
  jobPayments: JobPayment[];
  customer: Customer;
  onChange: (change: any) => void;
  preButtonEmailText: string;
  postButtonEmailText: string;
};
const CustomerEmailPreview: FC<Props> = ({
  emailTemplate,
  job,
  jobContent,
  jobPayments,
  customer,
  onChange,
  preButtonEmailText,
  postButtonEmailText,
}: Props) => {
  const myProfile = useSelector((state: any) => state.users.myProfile);

  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  const companyName = useMemo(
    () => myProfile?.accounts?.[0]?.companyName,
    [myProfile]
  );

  const logoUrl = useMemo(() => myProfile?.accounts?.[0]?.logoUrl, [myProfile]);

  const jobType = useMemo(() => getSpecificJobTypeText(job.status), [job]);

  const amountDue = useMemo(() => {
    if (jobType === JobTypeSpecificText.INVOICE) {
      const jobTotal = jobContent.total;
      const amountPaid = getTotalCompletedPaymentValueFromJobPayments(
        jobPayments || []
      );
      return currencyPrice(jobTotal - amountPaid, account.region);
    }
    return '';
  }, [jobContent, jobPayments, jobType, account]);

  const dueDate = useMemo(() => {
    // due at exists on sent jobs, otherwise use dueInOrValidForDays
    return job.dueAt
      ? moment(job.dueAt).format(MOMENT_DATE_SHORT_READABLE_FORMAT)
      : moment()
          .add(job.dueInOrValidForDays, 'days')
          .format(MOMENT_DATE_SHORT_READABLE_FORMAT);
  }, [job]);

  const jobContact = useMemo(
    () => getJobContactFromCustomer(customer, job.contactId),
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'job.contactId'
    [customer]
  );

  const email = useMemo(() => {
    let email = emailTemplate
      ?.replaceAll('[customer_name]', jobContact?.name || '')
      .replaceAll(
        '[customer_first_name]',
        jobContact?.name?.split(' ')[0] || ''
      )
      .replaceAll('[reference]', job.customReference || String(job.reference))
      .replaceAll('[company_name]', companyName);
    if (jobType === JobTypeSpecificText.INVOICE) {
      email = email
        ?.replaceAll('[amount_due]', amountDue)
        .replaceAll('[due_date]', dueDate);
    }
    return email || '';
  }, [
    amountDue,
    companyName,
    dueDate,
    emailTemplate,
    jobContact,
    jobType,
    job,
  ]);

  useEffect(() => {
    // set email from email template
    let splitEmail;
    if (email.includes('\n[button]\n')) {
      splitEmail = email.split('\n[button]\n');
    } else {
      splitEmail = email.split('[button]');
    }
    onChange({
      preButtonEmailText: splitEmail[0],
      postButtonEmailText: splitEmail[1],
    });
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'onChange'
  }, [email]);

  return (
    <div className="customer-email-preview-wrapper">
      <div className="customer-email-preview">
        {logoUrl && <img className="logo" src={logoUrl} />}
        {/* Pre button text */}
        <TextareaField
          name="preButtonEmailText"
          value={preButtonEmailText}
          onChange={(change: any) => onChange(change)}
          autoSize={true}
        />

        {/* View job button */}
        <Button isDisabled={true}>View {jobType}</Button>

        {/* Post button text */}
        <TextareaField
          name="postButtonEmailText"
          value={postButtonEmailText}
          onChange={(change: any) => onChange(change)}
          autoSize={true}
        />
      </div>
    </div>
  );
};

export default CustomerEmailPreview;
